import React, { FunctionComponent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import { isEmpty } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  LYRA_SELECT_ZENDESK_ARTICLE_URL,
  SELF_PAY_ZENDESK_ARTICLE_URL,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  getPossessivePronoun,
  getReflexivePronoun,
  isAgeTeen,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { isDAMinorProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { useSessionsLabel } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/hooks/useSessionsLabel'
import { CareProgressData, ConcurrentCareEpisode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'

import { CareOnboardingStatus } from './CareOnboardingStatus'
import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { BulletedList, BulletItem } from '../../atoms/bulletedList/BulletedList'
import { Divider } from '../../atoms/divider/Divider'
import { IconBadge } from '../../atoms/iconBadge/IconBadge'
import { ExternalLinkIcon } from '../../atoms/icons/ExternalLinkIcon'
import { SparkleGradient } from '../../atoms/icons/SparkleGradient'
import { Link } from '../../atoms/link/Link'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { SummaryDetailProps } from '../../atoms/summaryDetail/SummaryDetail'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { PAGE_SCROLLBAR_WIDTH_CSS_VAR } from '../../hooks/useClientProfileHeaderCollapsed'
import { AIConversationToggleRow } from '../../molecules/aiCoachToggle/AICoachToggle'
import { ConcurrentCare } from '../../molecules/concurrentCare/ConcurrentCare'
import { InlineWarningBanner } from '../../molecules/inlineWarningBanner/InlineWarningBanner'
import { ParentIntakeFormLink } from '../../molecules/parentIntakeFormLink/ParentIntakeFormLink'
import { SummaryDetails } from '../../molecules/summaryDetails/SummaryDetails'
import { SummarySection } from '../../molecules/summarySection/SummarySection'
import { openUrl } from '../../utils/expo-linking'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { AIConversationsSidePanelSection } from '../clientDetailsSidePanel/aiConversations/AIConversationsSidePanelSection'
import { CareServicesSidePanelSection } from '../clientDetailsSidePanel/careServices/CareServicesSidePanelSection'
import { CareServicesSidePanelData } from '../clientDetailsSidePanel/careServices/types'
import { ClientDetailsSidePanel, useSidePanelWidth } from '../clientDetailsSidePanel/ClientDetailsSidePanel'
import { ContactsSidePanelSection } from '../clientDetailsSidePanel/contacts/ContactsSidePanelSection'
import { ContactsSidePanelData } from '../clientDetailsSidePanel/contacts/types'
import {
  MedicationSidePanelItem,
  MedicationsSidePanelSection,
} from '../clientDetailsSidePanel/medications/MedicationsSidePanelSection'
import {
  SponsoredBenefitsSidePanelAction,
  SponsoredBenefitsSidePanelData,
  SponsoredBenefitsSidePanelSection,
} from '../clientDetailsSidePanel/sponsoredBenefits/SponsoredBenefitsSidePanelSection'
import { EligibilityGroupCapsule } from '../eligibilityGroupCapsule/EligiblityGroupCapsule'

export type BenefitsSummaryProps = {
  benefitsResetDate: string
  benefitsSummaryDetails: SummaryDetailProps[]
  eligibilityCapsuleDetails: ({
    testID: string
    program: string
    label: string
    coverageDescription: string | undefined
    tooltip?: string
    hasLink: boolean
    link: string | undefined
    isLyraProgram: boolean
  } | null)[]
  customerName: string
  sessionSummaryDetails?: SummaryDetailProps[]
  sessionSummaryTitle: string
  parentIntakeLink?: string
  shouldDisplayParentIntakeLink?: boolean
  isLyraSelect: boolean
  hasEligibilityError: boolean
  clientFirstName?: string
  healthPlanStatus?: React.ReactNode
  selfPayContinuationOfCareStatus?: React.ReactNode
  selfPayEligible?: boolean
  hpiSelfPayWarningBanner?: React.ReactNode
  careProgressSummaryData: CareProgressData
  sessionsNeedingNotes?: number[]
  concurrentCare: ConcurrentCareEpisode[]
  combineProgramSectionsAndShowReferral?: boolean
  onReferralFormPress: () => void
  isDAEngageProvider: boolean
  clientAge?: number
  clientPronouns?: string[]
  onOpenChat?: () => void
  showIsTeenLedProfileBanner?: boolean
  showAdolescentTurning18Banner?: boolean
  eighteenthBirthdayFormatted?: string
  programName?: string
  clinicalQualityInsights?: BulletItem[]
  upcomingSessionsModule?: React.ReactNode
  tasksModule?: React.ReactNode
  hasTasksModuleItems?: boolean
  riskAlert?: React.ReactNode
  riskSummary?: React.ReactNode
  recommendedActions?: React.ReactNode
  showSummaryCard?: boolean
  shouldHideEligiblePrograms?: boolean
  showSidePanel?: boolean
  sponsoredBenefitsSidePanelData?: SponsoredBenefitsSidePanelData
  aiConversationToggleRows?: AIConversationToggleRow[]
  careServicesSidePanelData?: CareServicesSidePanelData
  onViewPastCare?: () => void
  onMessageCareProvider?: (providerId: string) => void
  contactsSidePanelData?: ContactsSidePanelData
  medicationsSidePanelData?: readonly MedicationSidePanelItem[]
  onViewServicesAndBenefitsPress?: () => void
}

const CTAButtonContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginLeft: 'auto',
  alignSelf: 'flex-start',
  paddingLeft: spacing['16px'],
}))

const UpcomingSessionsModuleWrapper = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['24px'],
}))

const TasksModuleWrapper = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['24px'],
}))

const RiskAlertWrapper = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['32px'],
}))

const RiskSummaryWrapper = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['32px'],
}))

const SummaryHeaderContainer = styled.View<{ showSidePanel: boolean }>(({ theme, showSidePanel }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  ...(showSidePanel && { marginHorizontal: theme.spacing['16px'] }),
}))

const EligibleProgramsContainer = styled.View<{ showSidePanel: boolean }>(({ theme, showSidePanel }) => ({
  ...(showSidePanel && { marginHorizontal: theme.spacing['16px'] }),
}))

const LyraCareProgramsContainer = styled.View<{ showSidePanel: boolean }>(({ theme, showSidePanel }) => ({
  ...(showSidePanel && { marginTop: theme.spacing['32px'] }),
}))

const SummarySectionContainer = styled.View({
  flex: 1,
})

const BenefitsSummaryLayout = styled.View({
  flexDirection: 'row',
  alignItems: 'stretch',
})

const BenefitsSummaryContent = styled.View<{
  theme: ThemeType
  showSidePanel: boolean
  sidePanelWidth: number
}>(({ theme: { spacing }, showSidePanel, sidePanelWidth }) => ({
  flex: 1,
  minWidth: 0,
  margin: spacing['40px'],
  ...(showSidePanel && {
    marginRight: `calc(${sidePanelWidth}px + var(${PAGE_SCROLLBAR_WIDTH_CSS_VAR}, 0px) + ${spacing['40px']})`,
  }),
}))

const SummaryCard = styled.View(({ theme: { colors, spacing } }) => ({
  backgroundColor: colors.backgroundSection,
  borderRadius: spacing['16px'],
  borderWidth: 1,
  borderColor: colors.borderDefault,
  borderStyle: 'solid',
  padding: spacing['24px'],
}))

const TeenLedBannerMessageContainer = styled.View({
  display: 'block',
})

const AIGeneratedBadgeContainer = styled.View(({ theme }) => ({
  alignItems: 'flex-start',
  marginBottom: theme.spacing['16px'],
}))

export const BenefitsSummary: FunctionComponent<BenefitsSummaryProps> = ({
  customerName,
  benefitsResetDate,
  benefitsSummaryDetails,
  eligibilityCapsuleDetails,
  sessionSummaryDetails = [],
  sessionSummaryTitle,
  parentIntakeLink,
  shouldDisplayParentIntakeLink,
  isLyraSelect,
  hasEligibilityError,
  clientFirstName,
  healthPlanStatus,
  selfPayEligible,
  selfPayContinuationOfCareStatus,
  hpiSelfPayWarningBanner,
  careProgressSummaryData,
  concurrentCare,
  sessionsNeedingNotes,
  combineProgramSectionsAndShowReferral,
  onReferralFormPress,
  isDAEngageProvider,
  clientAge,
  clientPronouns,
  onOpenChat,
  showIsTeenLedProfileBanner = false,
  showAdolescentTurning18Banner = false,
  eighteenthBirthdayFormatted,
  programName,
  clinicalQualityInsights = [],
  upcomingSessionsModule,
  tasksModule,
  hasTasksModuleItems = false,
  riskAlert,
  riskSummary,
  recommendedActions,
  showSummaryCard = false,
  shouldHideEligiblePrograms = false,
  showSidePanel = false,
  sponsoredBenefitsSidePanelData,
  aiConversationToggleRows = [],
  careServicesSidePanelData,
  onViewPastCare,
  onMessageCareProvider,
  contactsSidePanelData,
  medicationsSidePanelData,
  onViewServicesAndBenefitsPress,
}) => {
  const { colors } = useTheme()
  const sidePanelWidth = useSidePanelWidth()

  const theme = useTheme()
  const intl = useIntl()
  const { formatMessage } = intl
  const sessionsLabel = useSessionsLabel(sessionsNeedingNotes)

  const isDAMinorProgramClient = programName ? isDAMinorProgram(programName) : false

  const isFamilyMinorClient =
    (Programs.getProgramConfigByName(programName)?.hidesEligibleServicesForMinors ?? false) &&
    clientAge !== undefined &&
    clientAge < 18

  const shouldHideEligibleServices = isDAMinorProgramClient || isFamilyMinorClient
  const showViewServicesAndBenefitsLink =
    Boolean(onViewServicesAndBenefitsPress) && !shouldHideEligibleServices && !shouldHideEligiblePrograms

  const lyraCarePrograms = eligibilityCapsuleDetails.filter((entry) => entry?.isLyraProgram)
  const extendedPrograms = eligibilityCapsuleDetails.filter((entry) => !entry?.isLyraProgram)

  const { cta, summary, isAIGenerated, careOnboardingStatus } = careProgressSummaryData

  const hasUpcomingSessionsModule = !!upcomingSessionsModule

  const AIIconBadge = (
    <IconBadge
      text={intl.formatMessage({
        defaultMessage: 'AI Generated',
        description: 'Badge to let user know that the associated section is generated by AI',
      })}
      frontIcon={<SparkleGradient size={13} color={colors.textSecondary} />}
      key='care-progress-summary-ai-generated-badge'
      preserveCase
      backgroundColor={colors.whiteIconButtonBackgroundDefault}
      style={{ paddingLeft: theme.spacing['4px'] }}
    />
  )

  const careProgressSummary = isAIGenerated ? (
    <>
      {summary}
      {AIIconBadge}
    </>
  ) : (
    summary
  )

  const providerSupportUrl = 'https://provider-support.lyrahealth.com/hc/en-us/requests/new'

  const handleSponsoredBenefitsActionPress = (action: SponsoredBenefitsSidePanelAction) => {
    switch (action) {
      case 'viewServicesAndBenefits':
        onViewServicesAndBenefitsPress?.()
        break
      case 'learnMoreAboutLyraSelect':
        openUrl(LYRA_SELECT_ZENDESK_ARTICLE_URL)
        break
      case 'submitEligibilitySupportTicket':
        openUrl(providerSupportUrl)
        break
    }
  }

  const SummarySectionWrapper = showSummaryCard ? SummaryCard : React.Fragment

  const content = (
    <View testID={tID('ClientSummary-tab')}>
      {riskAlert && <RiskAlertWrapper>{riskAlert}</RiskAlertWrapper>}
      <SummaryHeaderContainer showSidePanel={showSidePanel}>
        <SummarySectionContainer>
          <SummarySectionWrapper>
            <SummarySection
              key='next-session-summary'
              trailingHeader={
                cta.show && (
                  <CTAButtonContainer>
                    <Link
                      text={
                        <BodyText
                          testID={cta.testID}
                          text={cta.text}
                          size={BodyTextSize.SMALL}
                          color={colors.textActive}
                        />
                      }
                      onPress={cta.onPress}
                      underline
                    />
                  </CTAButtonContainer>
                )
              }
              testID={tID('SessionSummarySection')}
              title={sessionSummaryTitle}
              descriptionStyle={careOnboardingStatus ? { marginTop: theme.spacing['8px'] } : undefined}
              description={
                careOnboardingStatus ? (
                  <CareOnboardingStatus status={careOnboardingStatus} clientFirstName={clientFirstName} />
                ) : (
                  careProgressSummary
                )
              }
            >
              {recommendedActions}
              {clinicalQualityInsights.length > 0 && (
                <View testID={tID('ClinicalQualityInsights')} style={{ marginTop: theme.spacing['16px'] }}>
                  <Title
                    size={TitleSize.SMALL}
                    color={colors.textPrimary}
                    text={formatMessage(
                      {
                        defaultMessage: 'Ideas to consider with {clientName}:',
                        description: 'Header text for clinical quality insights section',
                      },
                      { clientName: clientFirstName ?? 'this client' },
                    )}
                  />
                  <BodyText
                    size={BodyTextSize.DEFAULT}
                    color={colors.textSecondary}
                    text={formatMessage(
                      {
                        defaultMessage:
                          'Based on your sessions with {clientName} so far, here are some things you might consider incorporating, if it fits with your clinical judgement and the plan of care.',
                        description: 'Intro text for clinical quality insights section',
                      },
                      { clientName: clientFirstName ?? 'this client' },
                    )}
                  />
                  <BulletedList
                    data={clinicalQualityInsights}
                    size={BodyTextSize.DEFAULT}
                    bulletColor={colors.textSecondary}
                    centerAlignBullet
                    spaceBetween={0}
                  />
                  <AIGeneratedBadgeContainer>{AIIconBadge}</AIGeneratedBadgeContainer>
                </View>
              )}
              {!isEmpty(sessionsNeedingNotes) && !hasTasksModuleItems && (
                <InlineWarningBanner
                  testId={tID('ClientSummary-completeNotesBanner')}
                  text={
                    <FormattedMessage
                      defaultMessage='Complete {count, plural, one {note} other {notes}} for {sessionsLabel}.'
                      description='Prompt to complete notes for specific sessions; pluralizes note'
                      values={{
                        count: sessionsNeedingNotes?.length ?? 0,
                        sessionsLabel,
                      }}
                    />
                  }
                />
              )}
            </SummarySection>
          </SummarySectionWrapper>
        </SummarySectionContainer>
      </SummaryHeaderContainer>
      {hasUpcomingSessionsModule && (
        <UpcomingSessionsModuleWrapper>{upcomingSessionsModule}</UpcomingSessionsModuleWrapper>
      )}
      {tasksModule && <TasksModuleWrapper>{tasksModule}</TasksModuleWrapper>}
      {!hasUpcomingSessionsModule && !isEmpty(sessionSummaryDetails) && (
        <SummaryDetails key='next-session-sd' summaryDetails={sessionSummaryDetails} keyPrefix='next-session' />
      )}
      {riskSummary && <RiskSummaryWrapper>{riskSummary}</RiskSummaryWrapper>}
      {showIsTeenLedProfileBanner && (
        <InlineWarningBanner
          testId={tID('ClientSummary-teenLedContactCaregiverBanner')}
          text={
            <>
              <TeenLedBannerMessageContainer>
                {formatMessage(
                  {
                    defaultMessage:
                      '<bold>{clientFirstName} ({ageLabel}{age}) booked care {reflexivePronoun}. Would you like to involve {possessivePronoun} caregiver?</bold>',
                    description: 'Information banner for minor clients who booked care for themselves',
                  },
                  {
                    clientFirstName,
                    ageLabel: clientAge !== undefined && isAgeTeen(clientAge) ? 'teen/age ' : 'age ',
                    age: clientAge,
                    reflexivePronoun: getReflexivePronoun(clientPronouns),
                    possessivePronoun: getPossessivePronoun(clientPronouns),
                    bold: (chunks: React.ReactNode) => (
                      <BodyText size={BodyTextSize.DEFAULT} bold color={colors.textWarning}>
                        {chunks}
                      </BodyText>
                    ),
                  },
                )}
              </TeenLedBannerMessageContainer>
              {onOpenChat && (
                <TeenLedBannerMessageContainer>
                  {formatMessage(
                    {
                      defaultMessage:
                        "If so, <link>message {firstName}</link> to discuss and confirm the caregiver's contact information.",
                      description: 'Second line text for teen-led profile banner with message link',
                    },
                    {
                      firstName: clientFirstName,
                      link: (chunks: React.ReactNode) => (
                        <Link
                          text={chunks}
                          onPress={onOpenChat}
                          underline
                          color={colors.components.inlineWarningBanner.text}
                        />
                      ),
                    },
                  )}
                </TeenLedBannerMessageContainer>
              )}
            </>
          }
          marginTop={theme.spacing['16px']}
          marginBottom={theme.spacing['16px']}
        />
      )}
      {showAdolescentTurning18Banner && (
        <InlineWarningBanner
          testId={tID('ClientSummary-adolescentTurning18Banner')}
          text={formatMessage(
            {
              defaultMessage:
                '{clientFirstName} turns 18 on {eighteenthBirthday}. {clientFirstName} must contact care@lyrahealth.com or 877-505-7147 to set up their own adult account for future sessions. You are responsible for collecting and storing a new consent to care form in the event of a records request.',
              description:
                'Information banner for adolescent clients approaching 18, informing providers about account transition requirements',
            },
            {
              clientFirstName,
              eighteenthBirthday: eighteenthBirthdayFormatted,
            },
          )}
          marginTop={theme.spacing['16px']}
          marginBottom={theme.spacing['16px']}
        />
      )}
      <ParentIntakeFormLink
        shouldDisplayParentIntakeLink={shouldDisplayParentIntakeLink}
        parentIntakeLink={parentIntakeLink}
      />

      <ConcurrentCare concurrentCare={concurrentCare} />

      {!showSidePanel && <Divider style={{ marginVertical: 40 }} />}

      {!showSidePanel && (
        <SummarySection
          key='benefits-summary'
          testID={tID('SessionsLimitSummarySection')}
          title={
            <FormattedMessage
              defaultMessage='Sponsored benefits • {customerName}'
              description='Heading for sponsored benefits and the employer name'
              values={{ customerName }}
            />
          }
          description={
            <FormattedMessage
              defaultMessage={`Benefits reset: {benefitsResetDate}`}
              description='Benefits reset date'
              values={{ benefitsResetDate }}
            />
          }
        >
          {isLyraSelect && (
            <InlineWarningBanner
              title={formatMessage({
                defaultMessage: 'Lyra Select Member',
                description: 'Title for Lyra Select client notice',
              })}
              text={formatMessage(
                {
                  defaultMessage:
                    '{clientFirstName} has a limited number of EAP sessions (6-8) and programs (coaching and individual therapy only) available to them.',
                  description: 'Description for limited session warning banner',
                },
                { clientFirstName },
              )}
              trailingLink={
                <Link
                  testID={tID('ClientSummary-lyraSelectClientNotice-learnMoreLink')}
                  color={theme.colors.textWarning}
                  text={
                    <BodyText
                      size={BodyTextSize.SMALL}
                      text={formatMessage({
                        defaultMessage: 'Learn more about Lyra Select.',
                        description: 'external link for Lyra Select client notice',
                      })}
                      color={theme.colors.textWarning}
                    />
                  }
                  onPress={() => openUrl(LYRA_SELECT_ZENDESK_ARTICLE_URL)}
                  underline
                />
              }
              textSize={BodyTextSize.SMALL}
              marginTop='-8px'
              marginBottom='16px'
              testId='ClientSummary-lyraSelectClientNotice'
              textColor={theme.colors.textWarning}
              iconColor={theme.colors.textWarning}
            />
          )}
          {hpiSelfPayWarningBanner}
          {selfPayEligible && (
            <InlineWarningBanner
              testId={'BenefitsSummary-selfPayEligibleBanner'}
              text={formatMessage(
                {
                  defaultMessage:
                    '{clientFirstName} <warning>is almost out of covered sessions</warning>. {customerName} offers a self-pay option for clients to pay for sessions themselves. If you and {clientFirstName} agree to continue, <link>you can learn more about self-pay here.</link>',
                  description:
                    'Banner text informing that the client is almost out of covered sessions and they can use self-pay',
                },
                {
                  clientFirstName,
                  customerName,
                  link: (text: string) => (
                    <Link
                      size={BodyTextSize.SMALL}
                      style={{
                        textDecorationLine: 'underline',
                        color: colors.textWarning,
                      }}
                      onPress={() => openUrl(SELF_PAY_ZENDESK_ARTICLE_URL)}
                      text={text}
                    />
                  ),
                  warning: (text: string) => (
                    <BodyText
                      size={BodyTextSize.SMALL}
                      color={colors.textWarning}
                      style={{ fontWeight: '500' }}
                      text={text}
                    />
                  ),
                },
              )}
              textSize={BodyTextSize.SMALL}
              marginBottom='16px'
              textColor={theme.colors.textWarning}
              iconColor={theme.colors.textWarning}
            />
          )}
          {hasEligibilityError && clientFirstName && !isDAEngageProvider && (
            <InlineWarningBanner
              testId={'EligibilityErrorBanner'}
              text={
                <BodyText
                  text={
                    <FormattedMessage
                      defaultMessage={`{clientFirstName}’s eligibility not confirmed. Ask them to check their legal name and DOB in their profile. For more help, contact the Lyra Care Navigation Team. <bold>Do not schedule more sessions unless clinically necessary.</bold>`}
                      description='Message shown when eligibility is not confirmed'
                      values={{
                        clientFirstName,
                        bold: (chunks: React.ReactNode) => (
                          <BodyText size={BodyTextSize.SMALL} bold color={colors.textWarning}>
                            {chunks}
                          </BodyText>
                        ),
                      }}
                    />
                  }
                  size={BodyTextSize.SMALL}
                  color={colors.textWarning}
                />
              }
              marginTop={isLyraSelect ? '0px' : '-8px'}
              marginBottom='16px'
            />
          )}
          {hasEligibilityError && clientFirstName && isDAEngageProvider && (
            <InlineWarningBanner
              testId={'EligibilityErrorBanner'}
              text={
                <BodyText
                  text={
                    <FormattedMessage
                      defaultMessage={`{clientFirstName}’s eligibility not confirmed. Ask them to check their legal name and DOB in their profile. For more help, <link>submit a ticket</link> through the Help Center.`}
                      description='Message shown when eligibility is not confirmed and the Provider is a DA Engage Provider'
                      values={{
                        clientFirstName,
                        link: (chunks: React.ReactNode) => (
                          <Link
                            text={chunks}
                            onPress={(e) => {
                              e.preventDefault()
                              openUrl(providerSupportUrl)
                            }}
                            underline
                            size={BodyTextSize.SMALL}
                          />
                        ),
                      }}
                    />
                  }
                  size={BodyTextSize.SMALL}
                  color={colors.textWarning}
                />
              }
              marginTop={isLyraSelect ? '0px' : '-8px'}
              marginBottom='16px'
            />
          )}
          {!isEmpty(benefitsSummaryDetails) ? (
            <SummaryDetails
              key='benefits-sd'
              summaryDetails={benefitsSummaryDetails}
              keyPrefix='benefits'
              additionalInfo={selfPayContinuationOfCareStatus || healthPlanStatus}
            />
          ) : (
            <InlineWarningBanner
              text={formatMessage({
                defaultMessage: 'No coverage',
                description: 'Message when client has no coverage',
              })}
              backgroundColor={theme.colors.backgroundElement}
              textColor={theme.colors.textPrimary}
              withoutIcon
              padding='24px'
              marginBottom='0px'
            />
          )}
        </SummarySection>
      )}
      {!shouldHideEligibleServices &&
        !shouldHideEligiblePrograms &&
        !onViewServicesAndBenefitsPress &&
        (combineProgramSectionsAndShowReferral ? (
          <>
            <Divider style={{ marginVertical: 40 }} />
            <SummarySection
              headerHasRowOrientation
              testID={tID('EligibleServices')}
              title={
                <FormattedMessage
                  defaultMessage='Eligible services'
                  description='Heading for the client eligible programs'
                />
              }
              description={
                <FormattedMessage
                  defaultMessage='Complete a care referral form to connect {clientFirstName} to the eligible services below:'
                  description='Description for the eligible programs section'
                  values={{ clientFirstName }}
                />
              }
              rightSection={
                <SecondaryButton
                  testID={tID('EligibleServices-referralFormButton')}
                  text={
                    <FormattedMessage
                      defaultMessage='Care referral form'
                      description='Button to open the referral form'
                    />
                  }
                  rightIcon={<ExternalLinkIcon size={24} strokeColor={colors.iconActive} />}
                  onPress={onReferralFormPress}
                />
              }
            />
            <EligibilityGroupCapsule eligibilityCapsuleDetails={eligibilityCapsuleDetails} />
          </>
        ) : (
          <EligibleProgramsContainer showSidePanel={showSidePanel}>
            {!isEmpty(lyraCarePrograms) && (
              <>
                {!showSidePanel && <Divider style={{ marginVertical: 40 }} />}
                <LyraCareProgramsContainer showSidePanel={showSidePanel}>
                  <SummarySection
                    key='lyra-care-programs'
                    testID={tID('LyraCarePrograms')}
                    title={
                      <FormattedMessage
                        defaultMessage='Lyra Care programs'
                        description='Heading for the client eligible programs'
                      />
                    }
                    description={
                      <FormattedMessage
                        defaultMessage='To help {clientFirstName} search for care with another Lyra provider, copy a program link below:'
                        description='Description for the eligible programs section'
                        values={{ clientFirstName }}
                      />
                    }
                  />
                  <EligibilityGroupCapsule eligibilityCapsuleDetails={lyraCarePrograms} />
                </LyraCareProgramsContainer>
              </>
            )}
            {!isEmpty(extendedPrograms) && (
              <>
                <View style={{ height: 24 }} />
                <SummarySection
                  key='more-eligible-programs'
                  testID={tID('MoreEligiblePrograms')}
                  title={
                    <FormattedMessage
                      defaultMessage='More eligible programs'
                      description='Heading for the client additional eligible programs'
                    />
                  }
                />
                <EligibilityGroupCapsule eligibilityCapsuleDetails={extendedPrograms} />
              </>
            )}
          </EligibleProgramsContainer>
        ))}
    </View>
  )

  if (!showSidePanel) {
    return content
  }

  const showAIConversationsSidePanelSection = aiConversationToggleRows.length > 0
  const showCareServicesSidePanelSection = Boolean(careServicesSidePanelData?.programs.length || onViewPastCare)
  const guardian = contactsSidePanelData?.guardian
  const showContactsSidePanelSection = Boolean(
    guardian?.firstName || guardian?.lastName || guardian?.phone || guardian?.email,
  )

  return (
    <BenefitsSummaryLayout>
      <BenefitsSummaryContent showSidePanel={showSidePanel} sidePanelWidth={sidePanelWidth}>
        {content}
      </BenefitsSummaryContent>
      <ClientDetailsSidePanel
        sponsoredBenefits={
          <SponsoredBenefitsSidePanelSection
            data={sponsoredBenefitsSidePanelData}
            onActionPress={handleSponsoredBenefitsActionPress}
            showViewServicesAndBenefitsLink={showViewServicesAndBenefitsLink}
          />
        }
        medications={
          medicationsSidePanelData?.length ? (
            <MedicationsSidePanelSection medications={medicationsSidePanelData} />
          ) : undefined
        }
        aiConversations={
          showAIConversationsSidePanelSection ? (
            <AIConversationsSidePanelSection rows={aiConversationToggleRows} />
          ) : undefined
        }
        careServices={
          showCareServicesSidePanelSection ? (
            <CareServicesSidePanelSection
              data={careServicesSidePanelData}
              onViewPastCare={onViewPastCare}
              onMessageProvider={onMessageCareProvider}
            />
          ) : undefined
        }
        contacts={
          showContactsSidePanelSection ? (
            <ContactsSidePanelSection data={contactsSidePanelData} clientFirstName={clientFirstName} />
          ) : undefined
        }
      />
    </BenefitsSummaryLayout>
  )
}
